/**
 * Badge Component
 * 
 * Small pill-shaped indicator for status, sentiment, and categories.
 * Essential for displaying call status, sentiment, direction in the table.
 * 
 * Variants automatically colored based on data:
 * - status: Call status (ended, ongoing, error)
 * - sentiment: User sentiment (Positive, Negative, Neutral)
 * - direction: Call direction (inbound, outbound)
 * - success: Success indicator (true/false)
 * - custom: Custom color
 * 
 * @example
 * <Badge variant="status" value={call.call_status}>Ended</Badge>
 * <Badge variant="sentiment" value="Positive">Positive</Badge>
 * <Badge variant="success" value={true}>Successful</Badge>
 */

import React from 'react';
import { getStatusColor, getSentimentColor, getDirectionColor } from '../../utils/helpers';

/**
 * Badge component
 * 
 * @param {object} props - Component props
 * @param {React.ReactNode} props.children - Badge text
 * @param {string} props.variant - Badge type (status, sentiment, direction, success, custom)
 * @param {string|boolean} props.value - Value to determine color (call_status, sentiment, etc.)
 * @param {string} props.size - Badge size (sm, md)
 * @param {boolean} props.dot - Show colored dot before text
 * @param {string} props.className - Additional CSS classes
 */
const Badge = ({
  children,
  variant = 'custom',
  value = null,
  size = 'sm',
  dot = false,
  className = '',
  ...rest
}) => {
  /**
   * Get color classes based on variant and value
   * Uses helper functions from utils/helpers.js
   */
  const getColorClasses = () => {
    switch (variant) {
      case 'status':
        // Get colors for call status (ended, ongoing, error, etc.)
        if (value) {
          const colors = getStatusColor(value);
          return `${colors.bg} ${colors.text} ${colors.border}`;
        }
        return 'bg-neutral-100 text-neutral-700 border-neutral-300';

      case 'sentiment':
        // Get colors for sentiment (Positive, Negative, Neutral, Unknown)
        if (value) {
          const colors = getSentimentColor(value);
          return `${colors.bg} ${colors.text} ${colors.border}`;
        }
        return 'bg-neutral-100 text-neutral-700 border-neutral-300';

      case 'direction':
        // Get colors for direction (inbound, outbound)
        if (value) {
          const colors = getDirectionColor(value);
          return `${colors.bg} ${colors.text} ${colors.border}`;
        }
        return 'bg-neutral-100 text-neutral-700 border-neutral-300';

      case 'success':
        // Green for success, red for failure
        if (value === true) {
          return 'bg-success-50 text-success-700 border-success-200';
        } else if (value === false) {
          return 'bg-error-50 text-error-700 border-error-200';
        }
        return 'bg-neutral-100 text-neutral-700 border-neutral-300';

      case 'custom':
      default:
        // Default neutral colors
        return 'bg-neutral-100 text-neutral-700 border-neutral-300';
    }
  };

  /**
   * Get size classes
   */
  const getSizeClasses = () => {
    switch (size) {
      case 'sm':
        return 'px-2 py-0.5 text-xs';
      
      case 'md':
        return 'px-3 py-1 text-sm';
      
      default:
        return 'px-2 py-0.5 text-xs';
    }
  };

  /**
   * Get dot color for the variant
   * Returns the text color class (which is darker than bg)
   */
  const getDotColor = () => {
    const colorClasses = getColorClasses();
    // Extract the text color class (e.g., "text-success-700")
    const textColorMatch = colorClasses.match(/text-[\w-]+/);
    return textColorMatch ? textColorMatch[0] : 'text-neutral-700';
  };

  // Combine all classes
  const badgeClasses = [
    // Base styles
    'inline-flex items-center',
    'font-medium',
    'rounded-full',
    'border',
    
    // Color classes
    getColorClasses(),
    
    // Size classes
    getSizeClasses(),
    
    // Custom classes
    className,
  ].join(' ');

  return (
    <span className={badgeClasses} {...rest}>
      {/* Optional colored dot */}
      {dot && (
        <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${getDotColor()}`} />
      )}
      
      {/* Badge text */}
      {children}
    </span>
  );
};

export default Badge;

